/** The home page's decisions, kept out of the components so node-only vitest can pin them.
 *  ⛔ No pay, salary or bank figure belongs on the home page; the restricted `payroll` and `finance` blocks are not a source. */

import { appForPath, isVisible } from './apps'

export function greetingKey(hour: number): 'homewall.morning' | 'homewall.afternoon' | 'homewall.evening' {
  if (hour < 12) return 'homewall.morning'
  if (hour < 18) return 'homewall.afternoon'
  return 'homewall.evening'
}

/** The first published roster day from `today` on, and its label. "Published" means `plan`, and a blank plan is a day with nothing on it. */
export function nextShift(
  schedule:
    | { days: { day: string; plan: string }[]; codes: { code: string; label: string }[] }
    | undefined,
  today: string,
): { day: string; code: string; isToday: boolean } | null {
  if (!schedule) return null
  const day = schedule.days.filter((d) => d.day >= today && d.plan.trim() !== '').sort((a, b) => a.day.localeCompare(b.day))[0]
  if (!day) return null
  const code = schedule.codes.find((c) => c.code === day.plan)?.label || day.plan
  return { day: day.day, code, isToday: day.day === today }
}

/** A quick-link tile shows only where its route is reachable: the sidebar's own gate for the app that owns the path, so a tile and its page cannot disagree. A path no app owns is hidden. */
export function quickLinkVisible(to: string, holds: (ability: string) => boolean): boolean {
  const app = appForPath(to.split('?')[0]!)
  return app ? isVisible(app, holds) : false
}

export type TimeLine = 'off' | 'hidden' | 'loading' | 'logged' | 'notLogged'

/** What the "today" time line says. A day off is told to everyone; the logged / not-logged state only to someone who can log time, so a viewer without `self.log` is never nagged about a page they cannot open. */
export function timeLine(input: { offToday: boolean; mayLog: boolean; loading: boolean; logged: boolean }): TimeLine {
  if (input.offToday) return 'off'
  if (!input.mayLog) return 'hidden'
  if (input.loading) return 'loading'
  return input.logged ? 'logged' : 'notLogged'
}

/** Rows held while loading so late lines don't shift the page: the next day off (holidays make it near-certain) and a roster member's shift; a desk is too rare. */
export function reservedStatusRows(input: { personalLoading: boolean; rosterLoading: boolean; onRoster: boolean }): number {
  return (input.personalLoading ? 1 : 0) + (input.rosterLoading && input.onRoster ? 1 : 0)
}
